import {
  WEBSITE_EMAIL_SOURCE_LABELS,
  WEBSITE_LOOKUP_STATUS_LABELS,
  type DiscoveryResultDTO,
} from '@hearth/shared';
import { AlertTriangle } from 'lucide-react';
import { Spinner, Tooltip } from '../../components/ui';
import { pagePath } from './model';

/**
 * The email line of a review row: the source listing's address, else what reading the place's own website found
 * (with the page it was published on — the evidence behind the implied consent), else why there is none.
 */
export function WebsiteEmail({ result }: { result: DiscoveryResultDTO }) {
  if (result.email) {
    return (
      <>
        <a href={`mailto:${result.email}`} className="text-ember-700 hover:underline">
          {result.email}
        </a>
        <span className="text-xs text-slate-400"> from the source</span>
      </>
    );
  }
  const lookup = result.websiteLookup;
  if (!lookup) return <span className="text-slate-400">no email in the source</span>;
  if (lookup.status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1.5 text-slate-500">
        <Spinner size={12} /> Looking on the website…
      </span>
    );
  }
  if (lookup.status !== 'found' || !lookup.email) {
    return (
      <Tooltip content={lookup.message}>
        <span className="text-slate-400">{WEBSITE_LOOKUP_STATUS_LABELS[lookup.status]}</span>
      </Tooltip>
    );
  }
  const others = lookup.candidates.filter((candidate) => candidate.email !== lookup.email);
  return (
    <>
      <a href={`mailto:${lookup.email}`} className="text-ember-700 hover:underline">
        {lookup.email}
      </a>
      {lookup.sourceUrl && (
        <span className="text-xs text-slate-500">
          {' from '}
          <a
            href={lookup.sourceUrl}
            target="_blank"
            rel="noreferrer"
            title={lookup.source ? `Found in the ${WEBSITE_EMAIL_SOURCE_LABELS[lookup.source]}` : undefined}
            className="hover:text-ember-700 hover:underline"
          >
            {pagePath(lookup.sourceUrl)}
          </a>
        </span>
      )}
      {others.length > 0 && (
        <>
          {' '}
          <Tooltip
            content={
              <ul>
                {others.map((candidate) => (
                  <li key={candidate.email}>
                    {candidate.email} ({pagePath(candidate.sourceUrl)})
                  </li>
                ))}
              </ul>
            }
          >
            <span tabIndex={0} className="cursor-default text-xs text-slate-500 underline decoration-dotted">
              +{others.length} more
            </span>
          </Tooltip>
        </>
      )}
      {lookup.noSolicitation && (
        <span className="mt-0.5 flex items-center gap-1 text-xs text-amber-700">
          <AlertTriangle className="size-3.5" /> The site asks for no solicitations — no consent will be
          recorded
        </span>
      )}
    </>
  );
}
